import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { renewals, files as filesApi } from './api';
import { authFetch } from './authSession';
import { computeSha256 } from './UploadModal';
import LinkToClientModal from './LinkToClientModal';

// =============================================================================
// v5.6.0 — Renewal workspace (the "notes pop-out")
// Design: proposal §4 (frontend) + Henry's decisions:
//   - opens from the row; loads GET /renewals/cycles/{id}/workspace
//   - two boxes, 5,000 characters each: Notes (outcome_notes) and Alternate Options
//   - Save / Cancel ONLY: no backdrop-click close, Escape is swallowed (Henry A)
//   - Save -> PATCH /renewals/cycles/{id}/notes with expected_updated_at, then one
//     PUT /files/metadata/{id} {notes} per changed per-file note (design D); a 409
//     shows the server's message and keeps what was typed (Henry #5)
//   - Cancel with unsaved changes -> "Discard changes?" (Henry #10)
//   - read-only rows (Hidden: superseded / duplicate) open read-only with the
//     reason, no Save, no client box / re-link (Henry Q2 / consolidated Q2)
// 12b: client box (link / change via LinkToClientModal), Documents (open, download,
//   per-file notes, upload via POST /cycles/{id}/files + sha256), and
//   onClientTreesChanged for anything that can create folders (delta 11).
// =============================================================================

export const NOTES_MAX = 5000;
// Count characters the way the server does (Python len = code points), not
// UTF-16 units, so an emoji counts once here and there.
export const charCount = (s) => Array.from(s || '').length;

export function readOnlyReasonText(reason) {
  if (!reason) return 'it is in the Hidden section';
  if (reason === 'duplicate') return 'it is marked as a duplicate';
  if (reason === 'rewritten') return 'it was rewritten to another company';   // 5.28.0
  const m = /^superseded_by_(\d{4})_term$/.exec(reason);
  if (m) return `it is an older term, superseded by the ${m[1]} term`;
  if (reason === 'same_year_duplicate') return 'it duplicates a current-term policy';
  return `it is in the Hidden section (${reason})`;
}

const errDetail = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return e?.message || 'Something went wrong';
};

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

// Open / download through authFetch (same endpoints as Main's previewFile /
// downloadFile, plus the 5.5.0 sign-in-again retry).
async function fetchBlob(url) {
  const res = await authFetch(url);
  if (!res.ok) throw new Error(res.status === 404 ? 'File not found on server' : `HTTP ${res.status}`);
  return res.blob();
}

export default function RenewalWorkspace({ cycleId, title, darkMode, onClose, onSaved, onChanged, onClientTreesChanged }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const subPanelBg = darkMode ? '#1d1d1d' : '#f7f7f7';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const inputBg = darkMode ? '#1a1a1a' : '#fff';

  const [ws, setWs] = useState(null);           // last server copy
  const [loadError, setLoadError] = useState(null);
  const [notes, setNotes] = useState('');
  const [alt, setAlt] = useState('');
  const [fileNotes, setFileNotes] = useState({});   // file id -> edited note text (only edited ones)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);     // shown above the footer; typed text is kept
  const [conflict, setConflict] = useState(false);
  const [linking, setLinking] = useState(false);
  const [uploads, setUploads] = useState([]);   // [{key, name, pct, state: 'hashing'|'uploading'|'done'|'error', msg}]
  const [docError, setDocError] = useState(null);
  const fileInput = useRef(null);
  const dirtyRef = useRef(false);

  // fresh=true replaces what is typed (first load / "Load latest"); otherwise only the
  // server copy (files, client, updated_at) is refreshed and typed text is kept.
  const load = useCallback(async (fresh = true) => {
    setLoadError(null);
    if (fresh) { setError(null); setConflict(false); }
    try {
      const r = await renewals.getCycleWorkspace(cycleId);
      setWs(r.data);
      if (fresh || !dirtyRef.current) {
        setNotes(r.data.outcome_notes || '');
        setAlt(r.data.alternate_options || '');
      }
      if (fresh) setFileNotes({});
    } catch (e) {
      setLoadError(e?.response?.status === 404
        ? "You don't have access to this renewal's notes, or it no longer exists."
        : `Couldn't load the notes: ${errDetail(e)}`);
    }
  }, [cycleId]);

  useEffect(() => { load(true); }, [load]);

  // Escape must never close the workspace (Save / Cancel only). Capture phase so
  // no other handler (e.g. an outer overlay) sees it either.
  useEffect(() => {
    const swallow = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('keydown', swallow, true);
    return () => window.removeEventListener('keydown', swallow, true);
  }, []);

  const readOnly = !!ws?.read_only;
  const linked = !!ws?.client_id;
  const origNotes = ws?.outcome_notes || '';
  const origAlt = ws?.alternate_options || '';
  const fileById = Object.fromEntries((ws?.files || []).map((f) => [f.id, f]));
  const changedFileNotes = Object.entries(fileNotes).filter(([id, v]) => (fileById[id]?.notes || '') !== v);
  const notesDirty = !!ws && (notes !== origNotes || alt !== origAlt);
  const dirty = notesDirty || changedFileNotes.length > 0;
  dirtyRef.current = notesDirty;
  const nCount = charCount(notes);
  const aCount = charCount(alt);
  const over = nCount > NOTES_MAX || aCount > NOTES_MAX
    || changedFileNotes.some(([, v]) => charCount(v) > NOTES_MAX);

  const treesChanged = (ids) => {
    const list = (ids || []).filter(Boolean);
    if (list.length && onClientTreesChanged) onClientTreesChanged(list);
  };

  const cancel = () => {
    if (dirty && !readOnly && !window.confirm('Discard changes?')) return;
    onClose();
  };

  const save = async () => {
    if (!ws || readOnly || saving || over) return;
    if (!dirty) { onClose(); return; }
    setSaving(true); setError(null); setConflict(false);
    try {
      if (notesDirty) {
        const body = { expected_updated_at: ws.updated_at };
        if (notes !== origNotes) body.outcome_notes = notes;
        if (alt !== origAlt) body.alternate_options = alt;
        const r = await renewals.updateCycleNotes(cycleId, body);
        setWs(r.data);                                   // new updated_at if a file note then fails
      }
      const failed = [];
      for (const [id, v] of changedFileNotes) {
        try { await filesApi.updateMetadata(Number(id), { notes: v }); }
        catch (e) { failed.push(`${fileById[id]?.filename || id}: ${errDetail(e)}`); }
      }
      if (failed.length) {
        setError(`Notes saved, but these document notes were not: ${failed.join('; ')}`);
        setSaving(false);
        return;
      }
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      // Keep everything that was typed; show the server's message.
      setError(errDetail(e));
      setConflict(e?.response?.status === 409 && /changed by someone else/i.test(errDetail(e)));
      setSaving(false);
    }
  };

  const reloadLatest = () => {
    if (window.confirm('Load the latest saved notes? What you typed here will be replaced — copy it first if you need it.')) load(true);
  };

  // --- client link (hidden on read-only rows) -------------------------------------
  const onLinked = async (data) => {
    treesChanged(data?.trees_changed);
    await load(false);
    if (onChanged) onChanged();
  };

  // --- documents -------------------------------------------------------------------
  const openFile = async (f) => {
    setDocError(null);
    try {
      const url = window.URL.createObjectURL(await fetchBlob(`/api/files/view/${f.id}`));
      window.open(url, '_blank');
    } catch (e) { setDocError(`Couldn't open ${f.filename}: ${e.message}`); }
  };
  const downloadFile = async (f) => {
    setDocError(null);
    try {
      const url = window.URL.createObjectURL(await fetchBlob(`/api/files/download/${f.id}`));
      const a = document.createElement('a'); a.href = url; a.download = f.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { setDocError(`Couldn't download ${f.filename}: ${e.message}`); }
  };

  const setUpload = (key, patch) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const uploadFiles = async (list) => {
    if (!list?.length || readOnly || !linked) return;
    setDocError(null);
    const items = Array.from(list).map((file, i) => ({ key: `${Date.now()}-${i}-${file.name}`, file, name: file.name, pct: 0, state: 'hashing', msg: '' }));
    setUploads((u) => [...u, ...items.map(({ file, ...rest }) => rest)]);
    let createdFolders = false;
    for (const it of items) {
      try {
        const sha = await computeSha256(it.file);
        setUpload(it.key, { state: 'uploading' });
        const r = await renewals.uploadCycleFile(cycleId, it.file, sha, (pct) => setUpload(it.key, { pct }));
        if (r.data?.created_folders?.length) createdFolders = true;
        setUpload(it.key, { state: 'done', pct: 100, msg: r.data?.file?.filename || '' });
      } catch (e) {
        setUpload(it.key, { state: 'error', msg: errDetail(e) });
      }
    }
    if (fileInput.current) fileInput.current.value = '';
    if (createdFolders) treesChanged([ws?.client_id]);
    await load(false);
    if (onChanged) onChanged();
  };

  const box = (label, value, setValue, count, testId) => (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 300px', minWidth: 0 }}>
      <label htmlFor={testId} style={{ fontSize: 12, fontWeight: 600, color: textSecondary, marginBottom: 4 }}>{label}</label>
      <textarea id={testId} data-testid={testId} value={value} readOnly={readOnly}
        onChange={(e) => setValue(e.target.value)}
        style={{ minHeight: 220, resize: 'vertical', padding: '8px 10px', fontSize: 13, lineHeight: 1.5,
          fontFamily: 'inherit', background: readOnly ? subPanelBg : inputBg,
          color: textPrimary, border: `1px solid ${count > NOTES_MAX ? '#C0392B' : borderColor}`, borderRadius: 6 }} />
      <div data-testid={`${testId}-count`} style={{ alignSelf: 'flex-end', fontSize: 11, marginTop: 3,
        color: count > NOTES_MAX ? '#C0392B' : textSecondary, fontWeight: count > NOTES_MAX ? 700 : 400 }}>
        {count.toLocaleString('en-US')} / {NOTES_MAX.toLocaleString('en-US')}
      </div>
    </div>
  );

  const btn = (primary) => ({
    padding: '7px 16px', fontSize: 13, borderRadius: 6, cursor: 'pointer', fontWeight: primary ? 600 : 400,
    background: primary ? '#185FA5' : panelBg, color: primary ? '#fff' : textPrimary,
    border: primary ? 'none' : `1px solid ${borderColor}`,
  });
  const smallBtn = { padding: '3px 10px', fontSize: 12, borderRadius: 5, cursor: 'pointer', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}` };

  const uploadsList = (ws?.files || []).filter((f) => f.kind === 'upload');
  const finishList = (ws?.files || []).filter((f) => f.kind === 'finish_notes');

  // No onClick on the backdrop: clicking outside does nothing (Henry A).
  return createPortal(
    <div data-testid="workspace-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="renewal-ws-title"
        style={{ background: panelBg, color: textPrimary, borderRadius: 10, border: `1px solid ${borderColor}`,
          boxShadow: '0 10px 40px rgba(0,0,0,0.35)', width: 1040, maxWidth: '100%', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}` }}>
          <div id="renewal-ws-title" style={{ fontSize: 16, fontWeight: 700 }}>📝 Renewal notes — {title || `cycle ${cycleId}`}</div>
          {ws && (
            <div data-testid="ws-client-box" style={{ fontSize: 12, color: textSecondary, marginTop: 5, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>Status: {ws.status}</span><span>·</span>
              <span>Client: <b style={{ color: textPrimary }}>{ws.client_name || 'not linked to a client'}</b></span>
              {!readOnly && (linked ? (
                <button data-testid="ws-change-client" onClick={() => setLinking(true)} style={smallBtn}>Change client</button>
              ) : (
                <button data-testid="ws-link-client" onClick={() => setLinking(true)}
                  style={{ ...smallBtn, background: '#185FA5', color: '#fff', border: 'none', fontWeight: 600 }}>
                  🔗 Link a client (required for documents)
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loadError && <div role="alert" style={{ color: '#C0392B', fontSize: 13 }}>{loadError}</div>}
          {!ws && !loadError && <div style={{ color: textSecondary, fontSize: 13 }}>Loading…</div>}
          {ws && readOnly && (
            <div data-testid="readonly-banner" style={{ background: darkMode ? '#3a3220' : '#FFF6E0', border: '1px solid #E0B44A',
              borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
              Read-only: {readOnlyReasonText(ws.read_only_reason)}. The notes and documents can be viewed but not changed.
            </div>
          )}
          {ws && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {box('Notes', notes, setNotes, nCount, 'ws-notes')}
              {box('Alternate Options (carriers already shopped)', alt, setAlt, aCount, 'ws-alt')}
            </div>
          )}

          {ws && (
            <div data-testid="ws-documents" style={{ borderTop: `1px solid ${borderColor}`, paddingTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Documents</div>
              {uploadsList.length === 0 && finishList.length === 0 && (
                <div style={{ fontSize: 12, color: textSecondary }}>No documents yet.</div>
              )}
              {uploadsList.map((f) => {
                const v = fileNotes[f.id] ?? (f.notes || '');
                return (
                  <div key={f.id} data-testid={`ws-file-${f.id}`} style={{ border: `1px solid ${borderColor}`, borderRadius: 6, padding: '8px 10px', marginBottom: 8, background: subPanelBg }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 13, wordBreak: 'break-all' }}>{f.filename}</span>
                      <span style={{ fontSize: 11, color: textSecondary }}>{[f.uploaded_by, fmtWhen(f.uploaded_at)].filter(Boolean).join(' · ')}</span>
                      <span style={{ flex: 1 }} />
                      <button onClick={() => openFile(f)} style={smallBtn}>Open</button>
                      <button onClick={() => downloadFile(f)} style={smallBtn}>Download</button>
                    </div>
                    <textarea data-testid={`ws-file-note-${f.id}`} value={v} readOnly={readOnly} placeholder={readOnly ? '' : 'Document note…'}
                      onChange={(e) => setFileNotes((m) => ({ ...m, [f.id]: e.target.value }))}
                      style={{ width: '100%', marginTop: 6, minHeight: 44, resize: 'vertical', padding: '5px 8px', fontSize: 12, fontFamily: 'inherit',
                        boxSizing: 'border-box', background: readOnly ? subPanelBg : inputBg, color: textPrimary,
                        border: `1px solid ${charCount(v) > NOTES_MAX ? '#C0392B' : borderColor}`, borderRadius: 5 }} />
                  </div>
                );
              })}
              {finishList.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: textSecondary, marginBottom: 4 }}>Finish notes (written automatically, read-only)</div>
                  {finishList.map((f) => (
                    <div key={f.id} data-testid={`ws-file-${f.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '3px 0' }}>
                      <span style={{ wordBreak: 'break-all' }}>{f.filename}</span>
                      <span style={{ flex: 1 }} />
                      <button onClick={() => openFile(f)} style={smallBtn}>Open</button>
                      <button onClick={() => downloadFile(f)} style={smallBtn}>Download</button>
                    </div>
                  ))}
                </div>
              )}
              {ws.queued_finish_notes > 0 && (
                <div style={{ fontSize: 12, color: '#B8860B', marginTop: 6 }}>
                  {ws.queued_finish_notes} finish-notes file(s) waiting — they are written when this renewal is linked to a client.
                </div>
              )}

              {!readOnly && (
                linked ? (
                  <div data-testid="ws-upload" style={{ marginTop: 10 }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}>
                    <input ref={fileInput} data-testid="ws-upload-input" type="file" multiple style={{ display: 'none' }}
                      onChange={(e) => uploadFiles(e.target.files)} />
                    <button onClick={() => fileInput.current && fileInput.current.click()} style={smallBtn}>＋ Add documents…</button>
                    <span style={{ fontSize: 11, color: textSecondary, marginLeft: 8 }}>
                      or drop files here — saved to {ws.client_name}'s Renewals folder for this year
                    </span>
                  </div>
                ) : (
                  <div data-testid="ws-upload-hint" style={{ fontSize: 12, color: textSecondary, marginTop: 10 }}>
                    Link this renewal to a client to add documents.
                  </div>
                )
              )}
              {uploads.length > 0 && (
                <div data-testid="ws-upload-status" style={{ marginTop: 8, fontSize: 12 }}>
                  {uploads.map((u) => (
                    <div key={u.key} data-testid={`ws-upload-row`} style={{ color: u.state === 'error' ? '#C0392B' : textSecondary }}>
                      {u.name}: {u.state === 'hashing' ? 'checking…' : u.state === 'uploading' ? `uploading ${u.pct}%` : u.state === 'done' ? `uploaded as ${u.msg}` : `failed — ${u.msg}`}
                    </div>
                  ))}
                </div>
              )}
              {docError && <div role="alert" style={{ color: '#C0392B', fontSize: 12, marginTop: 6 }}>{docError}</div>}
            </div>
          )}

          {error && (
            <div role="alert" data-testid="ws-error" style={{ color: '#C0392B', fontSize: 13, background: darkMode ? '#3a2020' : '#FDECEA',
              border: '1px solid #E6A19A', borderRadius: 6, padding: '8px 12px' }}>
              {error}
              {conflict && (
                <button onClick={reloadLatest} style={{ ...btn(false), marginLeft: 10, padding: '3px 10px', fontSize: 12 }}>
                  Load latest…
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {readOnly || loadError ? (
            <button onClick={onClose} style={btn(false)}>Close</button>
          ) : (
            <>
              <button onClick={cancel} disabled={saving} style={btn(false)}>Cancel</button>
              <button onClick={save} disabled={!ws || saving || over} style={{ ...btn(true), opacity: (!ws || saving || over) ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
      {linking && ws && (
        <LinkToClientModal
          policyId={ws.policy_id}
          currentName={title}
          currentClientId={ws.client_id}
          currentClientName={ws.client_name}
          darkMode={darkMode}
          onClose={() => setLinking(false)}
          onSaved={onLinked}
        />
      )}
    </div>,
    document.body,
  );
}
